import './image-with-focal-point.scss'
import type { CSSProperties, FC } from 'react'
import type { CmsImageWithFocalPoint } from '../../types/cmsImageWithFocalPoint'

type ImageDimensions = WidthAndHeightImageDimensions | WidthOnlyImageDimensions | HeightOnlyImageDimensions

interface WidthAndHeightImageDimensions {
    width: number
    height: number
}

interface WidthOnlyImageDimensions {
    width: number
    height?: number
}

interface HeightOnlyImageDimensions {
    width?: number
    height: number
}

interface DeviceSpecificImageDimensions {
    portrait?: ImageDimensions
    mobile?: ImageDimensions
    tablet?: ImageDimensions
    desktop?: ImageDimensions
}

export interface ImageWithFocalPointProps extends CmsImageWithFocalPoint {
    className?: string
    dimensions: ImageDimensions & DeviceSpecificImageDimensions
}

// eslint-disable-next-line complexity -- "complexity" comes from multiple conditional image src sets
export const ImageWithFocalPoint: FC<ImageWithFocalPointProps> = props => {
    const style: CSSProperties = {}

    const { width, height } = getDeviceWidthAndHeight(props)

    if (!(width && height)) {
        style.objectPosition = `${props.fromLeft ?? 50}% ${props.fromTop ?? 50}%`
    }

    const { width: widthPortrait, height: heightPortrait } = getDeviceWidthAndHeight(props, 'portrait')
    const { width: widthMobile, height: heightMobile } = getDeviceWidthAndHeight(props, 'mobile')
    const { width: widthTablet, height: heightTablet } = getDeviceWidthAndHeight(props, 'tablet')
    const { width: widthDesktop, height: heightDesktop } = getDeviceWidthAndHeight(props, 'desktop')

    return (
        <picture className="image-with-focal-point">
            {/* Set max-width as 1px less than media query break point, as the scss media query util counts resolutions
                as inclusive of their starting resolution, but exclusive of their upper limits. */}
            {props?.dimensions?.portrait && (
                <source
                    media="(max-width:479px)"
                    srcSet={getImgSrc(props, 'portrait')}
                    width={widthPortrait}
                    height={heightPortrait}
                />
            )}
            {props?.dimensions?.mobile && (
                <source
                    media="(max-width:767px)"
                    srcSet={getImgSrc(props, 'mobile')}
                    width={widthMobile}
                    height={heightMobile}
                />
            )}
            {props?.dimensions?.tablet && (
                <source
                    media="(max-width:1023px)"
                    srcSet={getImgSrc(props, 'tablet')}
                    width={widthTablet}
                    height={heightTablet}
                />
            )}
            {props?.dimensions?.desktop && (
                <source
                    media="(min-width:1024px)"
                    srcSet={getImgSrc(props, 'desktop')}
                    width={widthDesktop}
                    height={heightDesktop}
                />
            )}

            <img
                style={style}
                className={props.className}
                alt={props.alt}
                width={width}
                height={height}
                src={getImgSrc(props)}
                loading="lazy"
            />
        </picture>
    )
}

function getImgSrc(props: ImageWithFocalPointProps, imageType?: keyof DeviceSpecificImageDimensions) {
    const url = props.url.startsWith('/') ? `${window.location.origin}${props.url}` : props.url

    const urlObj = new URL(url)
    const params = new URLSearchParams(urlObj.search)

    const { width, height } = getDeviceWidthAndHeight(props, imageType)

    if (width) {
        params.set('width', width.toString())
    } else {
        params.delete('width')
    }

    if (height) {
        params.set('height', height.toString())
    } else {
        params.delete('height')
    }

    if (width && height) {
        const rxyLeft = (props.fromLeft ?? 50) / 100
        const rxyTop = (props.fromTop ?? 50) / 100

        /**
         * If dimensions have been specified for width and height, the image may be cropped.
         * The 'rxy' parameter specifies the focal point to crop around.
         *
         * If only one of width or height is specified, the image is only resized and not cropped.
         * In this case, the 'rxy' parameter would do nothing. To handle cropping in cases where the image is too wide or tall,
         * the object-position CSS property is used to make the focal point visible via the frontend.
         */
        params.set('rxy', `${rxyLeft},${rxyTop}`)
    }

    urlObj.search = params.toString()
    return urlObj.toString()
}

function getDeviceWidthAndHeight(props: ImageWithFocalPointProps, imageType?: keyof DeviceSpecificImageDimensions) {
    if (!imageType) {
        return { width: props.dimensions.width, height: props.dimensions.height }
    }

    const width = props.dimensions?.[imageType]?.width ?? props.dimensions.width
    const height = props.dimensions?.[imageType]?.height ?? props.dimensions.height

    return { width, height }
}
